"use client"

import type React from "react"
import { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Table,
  Badge,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap"
import { FaUser, FaEnvelope, FaPhone, FaIdCard, FaCheck, FaTimes } from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { PrintOrder } from "../BadgeRequest/badge-request"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  order: PrintOrder
  onOrderAction: () => void
}

const OrderDetailsModal: React.FC<Props> = ({ isOpen, toggle, order, onOrderAction }) => {
  const [loading, setLoading] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")
  const [showRejectionForm, setShowRejectionForm] = useState(false)

  // Calculate total amount from order details (amount is string from DecimalField)
  const calculateTotalAmount = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  // Check if order needs payment but is not paid
  const needsPayment = (order: PrintOrder): boolean => {
    const totalAmount = calculateTotalAmount(order)
    return order.status === "pending" && !order.is_postpaid && totalAmount > 0
  }

  const handleApprove = async () => {
    const result = await Swal.fire({
      title: "Approve Order?",
      text: "This will approve the order for badge printing.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      confirmButtonColor: "#28a745",
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)
      await BadgeRequestService.approveOrder(order.id)
      toast.success("Order approved successfully!")
      onOrderAction()
    } catch (error) {
      console.error("Failed to approve order:", error)
      toast.error("Failed to approve order")
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason")
      return
    }

    const result = await Swal.fire({
      title: "Reject Order?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      confirmButtonColor: "#dc3545",
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)
      await BadgeRequestService.rejectOrder(order.id, rejectionReason)
      toast.success("Order rejected successfully!")
      onOrderAction()
    } catch (error) {
      console.error("Failed to reject order:", error)
      toast.error("Failed to reject order")
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "paid":
        return "info"
      case "approved":
        return "success"
      case "printed":
        return "primary"
      case "cancelled":
        return "danger"
      case "postpaid":
        return "secondary"
    }
  }

  const totalAmount = calculateTotalAmount(order)
  const paymentRequired = needsPayment(order)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Order Details - {order.order_number}</ModalHeader>
      <ModalBody>
        {/* Order Summary */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={6}>
                <h6>Order Information</h6>
                <div className="mb-2">
                  <strong>Order Number:</strong> {order.order_number}
                </div>
                <div className="mb-2">
                  <strong>Status:</strong>{" "}
                  <Badge color={getStatusColor(order.status)} pill>
                    {order.status.toUpperCase()}
                  </Badge>
                </div>
                <div className="mb-2">
                  <strong>Total Amount:</strong> RWF {totalAmount.toLocaleString()}
                  {totalAmount === 0 && (
                    <Badge color="success" className="ms-2">
                      FREE
                    </Badge>
                  )}
                </div>
                <div className="mb-2">
                  <strong>Created:</strong> {new Date(order.created_at).toLocaleString()}
                </div>
                {order.is_postpaid && <Badge color="info">Postpaid Account</Badge>}
              </Col>
              <Col md={6}>
                <h6>Exhibition Information</h6>
                <div className="mb-2">
                  <strong>Exhibition:</strong> {order.participation_details?.exhibition_title}
                </div>
                <div className="mb-2">
                  <strong>Exhibitor:</strong> {order.participation_details?.exhibitor_name}
                </div>
                <div className="mb-2">
                  <strong>Stand Category:</strong> {order.participation_details?.stand_category_name}
                </div>
                <div className="mb-2">
                  <strong>Max Representatives:</strong> {order.participation_details?.num_representatives}
                </div>
              </Col>
              {/* ✅ Rejection Reason display */}
              {order.status === "cancelled" && order.reason && (
                <div className="mb-2">
                  <strong className="text-danger">Rejection Reason:</strong>
                  <span className="ms-2">{order.reason}</span>
                </div>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Payment Required Warning */}
        {paymentRequired && (
          <Card className="mb-4 border-warning">
            <CardBody>
              <div className="d-flex align-items-center">
                <Badge color="warning" className="me-3">
                  PAYMENT REQUIRED
                </Badge>
                <div>
                  <strong>Cannot Approve - Payment Pending</strong>
                  <div className="text-muted">
                    This order requires payment of RWF {totalAmount.toLocaleString()} before it can be approved.
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Payment Information */}
        {order.order_payments && order.order_payments.length > 0 && (
          <Card className="mb-4">
            <CardBody>
              <h6>Payment Information</h6>
              {order.order_payments.map((payment) => (
                <div key={payment.id} className="border rounded p-3 mb-2">
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Payment Method:</strong> {payment.payment_method.toUpperCase()}
                      </div>
                      <div className="mb-2">
                        <strong>Amount:</strong> RWF {payment.amount.toLocaleString()}
                      </div>
                    </Col>
                    <Col md={6}>
                      {payment.transaction_number && (
                        <div className="mb-2">
                          <strong>Transaction #:</strong> {payment.transaction_number}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Submitted:</strong> {new Date(payment.created_at).toLocaleString()}
                      </div>
                      {payment.proof_of_payment && (
                        <div>
                          <strong>Proof:</strong>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL}${payment.proof_of_payment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Document
                          </a>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {/* Representatives List */}
        <Card>
          <CardBody>
            <h6>Representatives ({order.order_details?.length || 0})</h6>
            <div className="table-responsive">
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>ID Number</th>
                    <th>Role</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.order_details?.map((detail) => (
                    <tr key={detail.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaUser className="me-2 text-muted" size={12} />
                          {detail.representation.person.first_name} {detail.representation.person.last_name}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaEnvelope className="me-2 text-muted" size={12} />
                          {detail.representation.person.email}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaPhone className="me-2 text-muted" size={12} />
                          {detail.representation.person.phone}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaIdCard className="me-2 text-muted" size={12} />
                          {detail.representation.person.id_doc_number}
                        </div>
                      </td>
                      <td>{detail.representation.role || "N/A"}</td>
                      <td>
                        {Number.parseFloat(detail.amount) > 0 ? (
                          <Badge color="primary">RWF {Number.parseFloat(detail.amount).toLocaleString()}</Badge>
                        ) : (
                          <Badge color="success">FREE</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Rejection Form */}
        {showRejectionForm && (
          <Card className="mt-4 border-danger">
            <CardBody>
              <h6 className="text-danger">Reject Order</h6>
              <FormGroup>
                <Label>Rejection Reason</Label>
                <Input
                  type="textarea"
                  rows={3}
                  placeholder="Please provide a reason for rejecting this order..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </FormGroup>
            </CardBody>
          </Card>
        )}
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
          {/* Only show action buttons if order can be acted upon */}
          {(order.status === "paid" || (order.status === "pending" && !paymentRequired)) && (
            <div className="d-flex gap-2">
              {!showRejectionForm ? (
                <>
                  <Button color="danger" onClick={() => setShowRejectionForm(true)} disabled={loading}>
                    <FaTimes className="me-2" size={12} />
                    Reject
                  </Button>
                  <Button color="success" onClick={handleApprove} disabled={loading}>
                    {loading ? <Spinner size="sm" className="me-2" /> : <FaCheck className="me-2" size={12} />}
                    Approve
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color="secondary"
                    onClick={() => {
                      setShowRejectionForm(false)
                      setRejectionReason("")
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button color="danger" onClick={handleReject} disabled={loading || !rejectionReason.trim()}>
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Confirm Rejection
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default OrderDetailsModal
